import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Missing Supabase environment variables! Check your .env file');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper function to generate unique IDs
function uid() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

// ============================================
// NEW SIMPLIFIED STATE MANAGEMENT
// ============================================

export async function loadStateFromSupabase() {
  try {
    console.log('🔄 Loading state from Supabase...');

    // Load all data in parallel
    const [usersRes, pendingUsersRes, studentsRes, announcementsRes, eventsRes, attendanceRes, codesRes] = await Promise.all([
      supabase.from('users').select('*'),
      supabase.from('pending_users').select('*'),
      supabase.from('students').select('*'),
      supabase.from('announcements').select('*'),
      supabase.from('events').select('*'),
      supabase.from('attendance').select('*'),
      supabase.from('access_codes').select('*')
    ]);

    // Check for errors
    if (usersRes.error) console.error('Error loading users:', usersRes.error);
    if (pendingUsersRes.error) console.error('Error loading pending users:', pendingUsersRes.error);
    if (studentsRes.error) console.error('Error loading students:', studentsRes.error);
    if (announcementsRes.error) console.error('Error loading announcements:', announcementsRes.error);
    if (eventsRes.error) console.error('Error loading events:', eventsRes.error);
    if (attendanceRes.error) console.error('Error loading attendance:', attendanceRes.error);
    if (codesRes.error) console.error('Error loading access codes:', codesRes.error);

    // Transform students array back into studentsByGrade object
    const studentsByGrade = {};
    (studentsRes.data || []).forEach(student => {
      if (!studentsByGrade[student.grade]) {
        studentsByGrade[student.grade] = [];
      }
      // Remove the grade field from student object, but keep name and parentInfo
      // Add a new id for the frontend (Supabase auto-generated id is not used)
      studentsByGrade[student.grade].push({
        id: uid(),
        name: student.name,
        parentInfo: student.parent_info || null
      });
    });

    console.log('📊 Loaded from Supabase:', {
      users: usersRes.data?.length || 0,
      students: (studentsRes.data || []).length,
      announcements: announcementsRes.data?.length || 0,
      grades: Object.keys(studentsByGrade).length
    });
    console.log('📚 Sample grades:', Object.keys(studentsByGrade).slice(0, 5));
    console.log('📝 Sample students from first grade:', studentsByGrade[Object.keys(studentsByGrade)[0]]?.slice(0, 3));

    // Transform users from snake_case to camelCase
    const users = (usersRes.data || []).map(user => ({
      email: user.email,
      username: user.username,
      password: user.password,
      name: user.name,
      role: user.role,
      gradeLevel: user.grade_level,
      title: user.title,
      isTeamLead: user.is_team_lead || false,
      teamLeadGrade: user.team_lead_grade
    }));

    const state = {
      users: users,
      pendingUsers: pendingUsersRes.data || [],
      studentsByGrade: studentsByGrade,
      announcements: announcementsRes.data || [],
      events: eventsRes.data || [],
      attendance: attendanceRes.data || [],
      accessCodes: codesRes.data || []
    };

    console.log('✅ State loaded from Supabase');
    return state;
  } catch (error) {
    console.error('❌ Error loading from Supabase:', error);
    return null;
  }
}

// ============================================
// USER PREFERENCES MANAGEMENT
// ============================================

export async function getUserPreferences(email) {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      // If no preferences exist, return defaults
      if (error.code === 'PGRST116') {
        return { darkMode: false, lastSeenAnnouncement: null };
      }
      console.error('Error loading user preferences:', error);
      return { darkMode: false, lastSeenAnnouncement: null };
    }

    return {
      darkMode: data.dark_mode || false,
      lastSeenAnnouncement: data.last_seen_announcement || null
    };
  } catch (error) {
    console.error('Error in getUserPreferences:', error);
    return { darkMode: false, lastSeenAnnouncement: null };
  }
}

export async function saveUserPreferences(email, preferences) {
  try {
    const dataToSave = {
      email: email,
      dark_mode: preferences.darkMode,
      last_seen_announcement: preferences.lastSeenAnnouncement,
      updated_at: new Date().toISOString()
    };

    // Use upsert to insert or update
    const { error } = await supabase
      .from('user_preferences')
      .upsert(dataToSave, { onConflict: 'email' });

    if (error) {
      console.error('Error saving user preferences:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in saveUserPreferences:', error);
    return false;
  }
}

export async function saveStateToSupabase(state) {
  try {
    console.log('💾 Saving state to Supabase...');

    // Transform studentsByGrade object into flat array with grade field
    const studentsArray = [];
    for (const [grade, students] of Object.entries(state.studentsByGrade || {})) {
      students.forEach(student => {
        // Only save students that have a name
        if (student && student.name) {
          studentsArray.push({
            name: student.name,
            grade: grade,
            parent_info: student.parentInfo || null
          });
        }
      });
    }

    console.log(`📊 Preparing to save: ${studentsArray.length} students, ${state.users?.length || 0} users`);
    console.log('Sample students:', studentsArray.slice(0, 3));

    // Save each table (upsert will insert or update based on unique constraints)
    const savePromises = [];

    // Users
    if (state.users && state.users.length > 0) {
      // Delete all existing users first, then insert new ones
      await supabase.from('users').delete().neq('email', '');

      // Map users to match Supabase schema
      const usersForSupabase = state.users.map(user => ({
        email: user.email,
        username: user.username || null,
        password: user.password,
        name: user.name,
        role: user.role,
        grade_level: user.gradeLevel || null,
        title: user.title || null,
        is_team_lead: user.isTeamLead || false,
        team_lead_grade: user.teamLeadGrade || null
      }));

      const { error: usersError } = await supabase.from('users').insert(usersForSupabase);
      if (usersError) {
        console.error('❌ Error saving users:', usersError);
      } else {
        console.log(`✅ Saved ${usersForSupabase.length} users to Supabase`);
      }
    }

    // Pending users
    if (state.pendingUsers && state.pendingUsers.length > 0) {
      await supabase.from('pending_users').delete().neq('id', '');
      savePromises.push(
        supabase.from('pending_users').insert(state.pendingUsers)
      );
    }

    // Students
    if (studentsArray.length > 0) {
      await supabase.from('students').delete().neq('name', '');
      const { error } = await supabase.from('students').insert(studentsArray);
      if (error) {
        console.error('❌ Error saving students:', error);
        console.log('Sample student data:', studentsArray.slice(0, 3));
      } else {
        console.log(`✅ Saved ${studentsArray.length} students to Supabase`);
      }
    }

    // Announcements
    if (state.announcements && state.announcements.length > 0) {
      await supabase.from('announcements').delete().neq('id', '');
      savePromises.push(
        supabase.from('announcements').insert(state.announcements)
      );
    }

    // Events
    if (state.events && state.events.length > 0) {
      await supabase.from('events').delete().neq('id', '');
      savePromises.push(
        supabase.from('events').insert(state.events)
      );
    }

    // Attendance
    if (state.attendance && state.attendance.length > 0) {
      await supabase.from('attendance').delete().neq('id', '');
      savePromises.push(
        supabase.from('attendance').insert(state.attendance)
      );
    }

    // Access codes
    if (state.accessCodes && state.accessCodes.length > 0) {
      await supabase.from('access_codes').delete().neq('code', '');
      savePromises.push(
        supabase.from('access_codes').insert(state.accessCodes)
      );
    }

    await Promise.all(savePromises);
    console.log('✅ State saved to Supabase');
  } catch (error) {
    console.error('❌ Error saving to Supabase:', error);
  }
}
